// src/api/vouchers.ts - Voucher related API calls

import { BASE_URL } from "../utils/config";
import { getAuthToken } from "../services/authService";
import { VoucherSubmissionPayload, VoucherResponse } from "../types/entry";
import { ClaimVoucherResponse } from "../types/api";
import { getSlug } from "../utils/domHelpers";

const claimKeys = new Map<string, string>();

/**
 * Submits a voucher code to redeem credits.
 * Requires authentication.
 * @param code - The voucher code to submit
 * @returns A promise resolving to the voucher response
 * @throws {Error} If the request fails or user is not authenticated
 */
export async function submitVoucherCode(
	code: string,
): Promise<VoucherResponse> {
	const url = `${BASE_URL}/vouchers/claim`;
	const token = getAuthToken();
	const hole_slug = getSlug(1);
	const claimIdentity = JSON.stringify([token, hole_slug, code]);
	const claimKey = claimKeys.get(claimIdentity) ?? crypto.randomUUID();
	claimKeys.set(claimIdentity, claimKey);

	const payload: VoucherSubmissionPayload = {
		voucher_code: code,
		hole_slug: hole_slug,
		idempotency_key: claimKey,
	};

	try {
		const response = await fetch(url, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${token ? token : ""}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
			credentials: "include",
		});

		const data = await response.json();

		if (!response.ok) {
			// Return error response with the detail message from API
			return {
				success: false,
				message:
					data.detail ||
					data.error ||
					data.message ||
					`Failed to redeem voucher: ${response.status}`,
			};
		}

		if (claimKeys.get(claimIdentity) === claimKey) {
			claimKeys.delete(claimIdentity);
		}

		return {
			success: true,
			entry_id: data.entry_id,
			hole_slug: data.hole_slug,
			tee: data.tee,
			recording_duration: data.recording_duration,
			status: data.status,
			message: data.message || "Voucher redeemed successfully",
			credits_added: data.credits_added,
			jackpot_credits: data.jackpot_credits,
			access_token: data.access_token,
			is_temp: data.is_temp,
		};
	} catch (error: any) {
		console.error("Error submitting voucher code:", error);
		return {
			success: false,
			message: error.message || "An error occurred while redeeming voucher.",
		};
	}
}
